/**
 * "U.S. documents" on the client's profile (S-31, owner decision 9): each US document once, with its
 * status and the action it needs. The tax form is reviewed and signed here, pre-filled from the
 * profile; the rest are on record and shown so a person sees one place for all of them.
 *
 * The statements the law asks at each signing (the non-U.S.-person certification, the accredited
 * affirmation, the own-account statement) stay on the signing screen, pre-filled from what is on file;
 * they are not listed here as something to redo.
 *
 * Shown only while the United States is switched on and the client has something to do with a U.S.
 * series: the server says `applies`.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import taxFormsService, {
  type SimpleDocumentItem,
  type TaxFormItem,
  type UsDocuments,
} from '../../services/taxForms.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { ROW_TITLE, changedThings, taxFormStatusLine } from '../../lib/taxForm';
import { colors } from '../../styles/theme';
import { calendarDate } from './enteringFund.helpers';
import TaxFormSigning, { signedMessage } from './TaxFormSigning';

function Row({
  title,
  status,
  detail,
  things = [],
  children,
}: Readonly<{
  title: string;
  status: string;
  detail?: string;
  /** What changed on the profile, one line each (a form that stopped being valid). */
  things?: readonly string[];
  children?: React.ReactNode;
}>): JSX.Element {
  return (
    <li className="py-3" style={{ borderTop: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>{title}</p>
          <p className="text-sm" style={{ color: colors.ink }}>{status}</p>
          {detail && <p className="text-xs mt-1" style={{ color: colors.muted }}>{detail}</p>}
          {things.length > 0 && (
            <ul className="text-xs mt-1 list-disc pl-5" style={{ color: colors.muted }}>
              {things.map((thing) => <li key={thing}>{thing}</li>)}
            </ul>
          )}
        </div>
        {children}
      </div>
    </li>
  );
}

function simpleRow(item: SimpleDocumentItem): JSX.Element {
  if (item.key === 'accreditation') {
    return (
      <Row
        key={item.key}
        title="Accreditation profile"
        status={item.expires_at ? `On file until ${calendarDate(item.expires_at.slice(0, 10))}` : 'On file'}
        detail="You confirm it again each time you sign for a U.S. series; what is on file only fills it in for you."
      />
    );
  }
  if (item.key === 'electronic_delivery') {
    return (
      <Row
        key={item.key}
        title="Consent to receive documents electronically"
        status="Given"
        detail="You can withdraw it from your Account Executive."
      />
    );
  }
  return (
    <Row
      key={item.key}
      title="Offering package"
      status={item.latest ? `Acknowledged on ${calendarDate(item.latest.slice(0, 10))}` : 'Acknowledged'}
      detail="Done at the signing of each series; nothing to repeat here."
    />
  );
}

export function TaxFormRow({
  item,
  onSign,
}: Readonly<{ item: TaxFormItem; onSign: () => void }>): JSX.Element {
  const line = taxFormStatusLine(item);
  async function download(): Promise<void> {
    if (!item.signature_id) return;
    const copy = await taxFormsService.hardCopy(item.signature_id);
    window.open(copy.url, '_blank', 'noopener');
  }
  return (
    <Row
      title={ROW_TITLE[item.form]}
      status={line.label}
      detail={line.detail}
      things={changedThings(item.reasons)}
    >
      <div className="flex flex-col gap-2 items-end flex-shrink-0">
        {line.action && (
          <button
            type="button"
            onClick={onSign}
            className="text-xs font-semibold px-3 py-1.5 rounded-md"
            style={{ background: colors.gold, color: colors.navy }}
          >
            {line.action}
          </button>
        )}
        {item.signature_id && (
          <button
            type="button"
            onClick={() => void download()}
            className="text-xs"
            style={{ color: colors.muted }}
          >
            Download my signed copy
          </button>
        )}
      </div>
    </Row>
  );
}

export default function UsDocumentsSection(): JSX.Element | null {
  const [docs, setDocs] = useState<UsDocuments | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [params] = useSearchParams();
  const productId = params.get('product');

  const load = useCallback(async () => {
    try {
      setDocs(await taxFormsService.usDocuments());
      setError(null);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, "We couldn't load your U.S. documents. Please try again shortly."));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  // A client sent here from a series they want to start opens the form at once.
  useEffect(() => {
    if (productId) setSigning(true);
  }, [productId]);
  // The banner and the series screens link to #us-documents; the section arrives after the page.
  useEffect(() => {
    if (docs && globalThis.location.hash === '#us-documents') {
      document.getElementById('us-documents')?.scrollIntoView({ block: 'start' });
    }
  }, [docs]);

  if (docs && !docs.applies && !productId) return null;

  return (
    <section
      id="us-documents"
      className="rounded-2xl p-6 max-w-2xl mt-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>U.S. documents</h2>
      <p className="text-xs mb-3" style={{ color: colors.muted }}>
        Each U.S. document once, with where it stands.
      </p>
      {error && (
        <p className="text-sm" role="alert" style={{ color: colors.danger }}>{error}</p>
      )}
      {done && (
        <output className="block text-sm mb-3" style={{ color: colors.successText }}>
          {done}
          {productId && (
            <>
              {' '}
              <Link to={`/client/products/${productId}`} style={{ color: colors.ink, fontWeight: 600 }}>
                Back to the series →
              </Link>
            </>
          )}
        </output>
      )}
      {signing ? (
        <TaxFormSigning
          productId={productId}
          onCancel={() => setSigning(false)}
          onSigned={(signed) => {
            setSigning(false);
            setDone(signedMessage(signed));
            void load();
          }}
        />
      ) : (
        <ul>
          {(docs?.items ?? []).map((item) =>
            item.key === 'tax_form' ? (
              <TaxFormRow key={item.key} item={item} onSign={() => setSigning(true)} />
            ) : (
              simpleRow(item)
            ),
          )}
        </ul>
      )}
    </section>
  );
}
